import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePromiseTracker } from 'react-promise-tracker';
import {
  useGridApiRef,
  GridPinnedColumns,
  GridColumnVisibilityModel,
  GridSortModel,
  GridColumns as XGridColumns,
} from '@mui/x-data-grid-pro';
import { XGrid } from '@AMIEWEB/Common';
import _ from 'lodash';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserGridPreference, selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import { selectPlacementPayPackagesPagination } from '@AMIEWEB/Order/Store/Order.selectors';
import { usePlacementPayPackagesFetch } from './usePlacementPayPackagesFetch';
import { defaultPinnedColumns, placementPayPackagesColumnVisibility, placementPayPackagesColumns } from './utils';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  placementMarginScenarioActions,
  placementMarginScenarioReducer,
  placementMarginScenarioSliceKey,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { placementMarginScenarioSaga } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.saga';
import {
  placementMarginPayPackageStatusActions,
  placementMarginPayPackageStatusSliceKey,
  placementMarginPayPackageStatusSliceReducer,
} from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.redux';
import { placementMarginPayPackageStatusSaga } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.saga';
import {
  placementMarginToolEmailAction,
  placementMarginToolEmailReducer,
  placementMarginToolEmailSliceKey,
} from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.redux';
import { placementMarginToolEmailSaga } from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.saga';
import {
  placementDetailReducer,
  placementDetailsAction,
  placementDetailSliceKey,
} from 'store/redux-store/placement-details/slice';
import { placementDetailsSaga } from 'store/redux-store/placement-details/saga';
import { IEmailType } from 'app/models/Notification/Notification';

export const PlacementPayPackagesGrid = ({ orderId }: { orderId: number }) => {
  const gridTag = `placement-pay-packages-grid-${orderId}`;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const emailIconField = 'emailIcon';

  // Inject MarginTool slices needed for "Email Candidate/Internal" actions (scoped to this grid/tab)
  useInjectReducer({ key: placementMarginScenarioSliceKey, reducer: placementMarginScenarioReducer });
  useInjectSaga({ key: placementMarginScenarioSliceKey, saga: placementMarginScenarioSaga });
  useInjectReducer({
    key: placementMarginPayPackageStatusSliceKey,
    reducer: placementMarginPayPackageStatusSliceReducer,
  });
  useInjectSaga({ key: placementMarginPayPackageStatusSliceKey, saga: placementMarginPayPackageStatusSaga });
  useInjectReducer({ key: placementMarginToolEmailSliceKey, reducer: placementMarginToolEmailReducer });
  useInjectSaga({ key: placementMarginToolEmailSliceKey, saga: placementMarginToolEmailSaga });
  useInjectReducer({ key: placementDetailSliceKey, reducer: placementDetailReducer });
  useInjectSaga({ key: placementDetailSliceKey, saga: placementDetailsSaga });

  const { promiseInProgress } = usePromiseTracker({ area: 'get-Placement-Pay-Packages' });

  // Use inline selector to avoid runtime issues if named selector imports are undefined (HMR/module edge cases)
  const sourceRows = useSelector((state: any) => state?.orderData?.placementPayPackagesSource ?? []);
  const pagination = useSelector(selectPlacementPayPackagesPagination);
  const fetchPlacementPayPackages = usePlacementPayPackagesFetch({ orderId });

  const gridPreferences = useSelector(selectUserGridPreference).find(item => item.id === gridTag);
  const userPreference = useSelector(selectUserPreference);
  const gridState = useSelector(selectGridState);

  const [pendingEmail, setPendingEmail] = useState<null | {
    placementId: number;
    scenarioId?: number;
    variant: string;
  }>(null);

  const selectedScenario = useSelector((state: any) => state?.[placementMarginScenarioSliceKey]?.selectedScenario);
  const bookingPeriod = useSelector((state: any) => state?.[placementMarginScenarioSliceKey]?.bookingPeriod);
  const emailRecipients = useSelector(
    (state: any) => state?.[placementMarginPayPackageStatusSliceKey]?.emailRecipients,
  );
  const placementHeader = useSelector((state: any) => state?.[placementDetailSliceKey]?.header);

  const onSelectEmailVariant = useCallback(
    (row: any, variant: string) => {
      const placementId = Number(row?.placementId);
      const scenarioId = row?.scenarioId != null ? Number(row?.scenarioId) : undefined;
      if (!Number.isFinite(placementId) || placementId <= 0) {
        dispatch(
          globalActions.setSnackBar({
            message: 'Unable to email: missing placement id.',
            severity: 'error',
          }),
        );
        return;
      }

      setPendingEmail({ placementId, scenarioId, variant });

      // Load the same data sources used by Margin Tool email flow
      dispatch(
        placementMarginScenarioActions.getPlacementMarginToolDetails({
          placementId,
          scenarioId: Number.isFinite(scenarioId) ? scenarioId : undefined,
        }),
      );
      dispatch(placementMarginPayPackageStatusActions.getPlacementMarginEmailRecipientsList({ placementId }));
      dispatch(
        placementDetailsAction.getHeaderDetailsAction({
          placementId,
        }),
      );

      // Mirror ScenarioHeader: mark email modal open + variant, then we will trigger the email creation once data is ready.
      dispatch(
        placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({
          openEmailModal: true,
          emailType: IEmailType.Manual,
          emailVariant: variant,
        }),
      );
    },
    [dispatch],
  );

  // Order context: hide Order ID column (redundant - already in order details)
  const buildOrderColumns = useCallback(() => {
    const base = placementPayPackagesColumns(t, { onSelectEmailVariant });
    return base.filter(c => c?.field !== 'orderId');
  }, [onSelectEmailVariant, t]);

  const orderColumnVisibilityModel = useMemo(() => {
    const base = _.cloneDeep(placementPayPackagesColumnVisibility);
    delete base.orderId;
    return base;
  }, []);

  const [columns, setColumns] = useState(buildOrderColumns());
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumns>(defaultPinnedColumns);
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(orderColumnVisibilityModel),
  );

  const rows = sourceRows || [];

  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (
      columnPreferences &&
      columnPreferences?.multiSortedColumns &&
      Array.isArray(columnPreferences?.multiSortedColumns)
    ) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };

  useEffect(() => {
    if (gridPreferences?.value?.multiSortedColumns?.length > 0) {
      setCurrentSortModel(getSortModelPreferences(gridPreferences?.value?.multiSortedColumns));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridPreferences]);

  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
      determineVisibilityModel();
      determineColumn();
      determinePinnedColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreference?.loaded, gridPreferences]);

  useEffect(() => {
    if (!pendingEmail) return;

    const headerReady = Number(placementHeader?.placementId) === Number(pendingEmail.placementId);
    const recipientsReady = emailRecipients != null;
    const scenarioReady =
      Number(selectedScenario?.placementId) === Number(pendingEmail.placementId) &&
      (pendingEmail.scenarioId == null || Number(selectedScenario?.scenarioId) === Number(pendingEmail.scenarioId));
    const bookingReady =
      bookingPeriod == null ||
      Number(bookingPeriod?.placementId) === Number(pendingEmail.placementId) ||
      Number(bookingPeriod?.placementId) === 0;

    if (headerReady && recipientsReady && scenarioReady && bookingReady) {
      dispatch(placementMarginToolEmailAction.handlePlacementMarginToolEmailCreation());
      setPendingEmail(null);
    }
  }, [
    pendingEmail,
    placementHeader?.placementId,
    emailRecipients,
    selectedScenario?.scenarioId,
    selectedScenario?.placementId,
    bookingPeriod?.placementId,
    dispatch,
  ]);

  const refreshPlacementPayPackages = () => {
    fetchPlacementPayPackages({
      pageNumber: gridState?.pageNumber || 1,
      pageSize: gridState?.pageSize || 20,
      sortModel: currentSortModel,
    });
  };

  const normalizePinnedColumns = React.useCallback(
    (value: GridPinnedColumns | undefined | null): GridPinnedColumns => {
      const left = Array.isArray(value?.left) ? [...value!.left] : [];
      const right = Array.isArray(value?.right) ? [...value!.right] : [];

      // Always keep email icon pinned and first
      const leftWithoutEmail = left.filter(f => f !== emailIconField);
      return {
        left: [emailIconField, ...leftWithoutEmail],
        right,
      };
    },
    [emailIconField],
  );

  const normalizeColumns = React.useCallback(
    (cols: any[]) => {
      const emailCol = cols.find(c => c?.field === emailIconField);
      const rest = cols.filter(c => c?.field !== emailIconField);
      return emailCol ? [emailCol, ...rest] : cols;
    },
    [emailIconField],
  );

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      const normalized = normalizePinnedColumns(updatedPinnedColumns);
      setPinnedColumns(normalized);
      const preferenceData = {
        id: gridTag,
        value: {
          ...(gridPreferences?.value || {}),
          pinnedColumns: normalized,
        },
      };
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    },
    [dispatch, gridTag, gridPreferences?.value, normalizePinnedColumns],
  );

  const resetColumns = () => {
    const defaultCols = buildOrderColumns();
    setColumns(defaultCols);
    setCurrentSortModel([]);
    setCurrentColumnVisibilityModel(_.cloneDeep(orderColumnVisibilityModel));
    setPinnedColumns(defaultPinnedColumns);

    const preferenceData = {
      id: gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: defaultCols,
        columnVisibilityModel: orderColumnVisibilityModel,
        pinnedColumns: defaultPinnedColumns,
        multiSortedColumns: [],
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    dispatch(
      globalActions.setSnackBar({
        message: 'Columns have been reset.',
        severity: 'success',
      }),
    );
  };

  const onSortModelChanged = (model: GridSortModel) => {
    saveSortModel(model);
    setCurrentSortModel(model);
    fetchPlacementPayPackages({
      pageNumber: 1,
      pageSize: gridState?.pageSize || 20,
      sortModel: model,
    });
  };

  const saveSortModel = React.useCallback(
    sortModel => {
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : undefined;
      if (!_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridPreferences?.value, columns?.length, gridState?.multiSortedColumns, currentColumnVisibilityModel],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!columns || columns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;
      if (currentGridColumns && currentGridColumns.length > 0 && !currentGridColumns[0].field)
        currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [userPreference.loaded, gridPreferences?.value, dispatch, columns],
  );

  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences && columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(orderColumnVisibilityModel);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };

  const determinePinnedColumns = () => {
    const pinnedColumn = (gridPreferences?.value?.pinnedColumns || defaultPinnedColumns) as GridPinnedColumns;
    setPinnedColumns(normalizePinnedColumns(pinnedColumn));
  };

  const determineColumn = () => {
    const currentColumn = normalizeColumns(getColumnPreferences(columns));
    setColumns(currentColumn);
  };

  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      if (columnPreferences && columnPreferences.columns) {
        const newColumns = cols.map(column => {
          const foundIndex = columnPreferences.columns.findIndex(item => item?.field === column?.field);
          if (foundIndex !== -1) {
            return {
              ...column,
              ...columnPreferences.columns[foundIndex],
            };
          }
          return column;
        });
        return newColumns.sort((a, b) => {
          const indexA = columnPreferences.columns.findIndex(i => i.field === a.field);
          const indexB = columnPreferences.columns.findIndex(i => i.field === b.field);
          return (
            (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) - (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
          );
        });
      }
      return cols;
    } catch (err) {
      return cols;
    }
  };

  return (
    <XGrid
      getRowId={row => row.id}
      gridTag={gridTag}
      columns={columns}
      apiRef={apiRef}
      rows={rows}
      autoHeight
      disableSelectionOnClick
      loading={promiseInProgress}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      defaultSortModel={currentSortModel}
      sortModel={currentSortModel}
      onSortModelChange={onSortModelChanged}
      paginationMode="server"
      title={t('order.payPackagesTab.placementPayPackages.title')}
      rowCount={pagination.totalCount}
      rowCountDisplay={pagination.totalCount}
      onPageChange={(page, details) => {
        fetchPlacementPayPackages({ pageNumber: page + 1, pageSize: gridState?.pageSize || 20 });
      }}
      onPageSizeChange={(size, details) => {
        fetchPlacementPayPackages({ pageNumber: 1, pageSize: size });
      }}
      defaultColumnVisibilityModel={orderColumnVisibilityModel}
      disableMultipleColumnsSorting={false}
      columnVisibilityModel={currentColumnVisibilityModel}
      onColumnVisibilityModelChange={(model, details) => {
        // Enforce: email icon column must always be visible
        const normalizedModel = { ...model, [emailIconField]: true };
        dispatch(gridStateActions.setColumnVisibilityModel(normalizedModel));
        setCurrentColumnVisibilityModel(_.cloneDeep(normalizedModel));
        savePreferences({
          field: undefined,
          columns: _.cloneDeep(apiRef?.current?.getAllColumns()),
          visibilityModel: _.cloneDeep(normalizedModel),
        });
      }}
      onColumnOrderChange={(params, event, details, _columns) => {
        savePreferences({
          field: params.field,
          columns: _columns,
          visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? orderColumnVisibilityModel),
        });
      }}
      onColumnResizeStop={_columns => {
        savePreferences({
          field: undefined,
          columns: _columns,
          visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? orderColumnVisibilityModel),
        });
      }}
      toolbarProps={{
        refreshButton: true,
        filterButton: true,
        resetButton: true,
        refreshGrid: () => refreshPlacementPayPackages(),
        resetColumns: resetColumns,
      }}
    />
  );
};
